import { useEffect, useState } from "react";
import {
  HUBSPOT_DIAGNOSIS_FORM_ID,
  sendLeadMail,
  submitHubSpotForm,
} from "../api/hubspot";

// 「3分でわかる無料AI診断」— サイト内で完結する成熟度診断。
// 6問(1問ずつ・進捗バー) → メール入力(必須) → その場で結果(A/B/Cフェーズ＋おすすめの一歩＋CTA)。
// リードはHubSpotへ送信済み。判定フェーズと回答内容は message に載せている。

const PRIMARY = "#2B7FFF";
const INK = "#1B2330";
const round = "'M PLUS Rounded 1c', sans-serif";

// ロゴ。以前は usagi AI 名義でうさぎのマークを使っていたが、
// メディアが LEAPS Media 名義になり繋がらなくなったため揃えた。
// マークは一旦サムネイルと同じ丸ドット。本来のロゴが決まったら差し替える。
function BrandMark({ size = 22, color = "#fff" }) {
  const d = Math.round(size * 0.55);
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: d,
        height: d,
        borderRadius: "50%",
        background: color,
        flexShrink: 0,
      }}
    />
  );
}

function ensureFonts() {
  if (document.getElementById("ai-lp-fonts")) return;
  const link = document.createElement("link");
  link.id = "ai-lp-fonts";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=M+PLUS+Rounded+1c:wght@400;500;700;800&family=Noto+Sans+JP:wght@400;500;700&display=swap";
  document.head.appendChild(link);
}

// score を持つ設問はスコア加算、null はタイプ分類のタグ用
const QUESTIONS = [
  {
    key: "size",
    q: "従業員規模を教えてください",
    options: [
      { label: "〜10名", score: null },
      { label: "10〜50名", score: null },
      { label: "50〜100名", score: null },
      { label: "100名以上", score: null },
    ],
  },
  {
    key: "usage",
    q: "現在のAI活用状況に近いのは？",
    options: [
      { label: "ほぼ使っていない", score: 0 },
      { label: "個人が試している", score: 1 },
      { label: "一部部署で業務利用", score: 2 },
      { label: "全社で日常的に活用", score: 3 },
    ],
  },
  {
    key: "data",
    q: "社内情報（資料・メール等）の状態は？",
    options: [
      { label: "あちこちに散らばっている", score: 0 },
      { label: "一部だけ整理されている", score: 1 },
      { label: "だいたい整理されている", score: 2 },
      { label: "ルール化され整備済み", score: 3 },
    ],
  },
  {
    key: "challenge",
    q: "いちばんの課題は？",
    options: [
      { label: "何から始めればいいか分からない", score: null },
      { label: "研修・導入しても現場に浸透しない", score: null },
      { label: "推進できる詳しい人がいない", score: null },
      { label: "必要な情報がすぐ探せない", score: null },
    ],
  },
  {
    key: "task",
    q: "AIに任せたい業務に近いのは？（複数選択可）",
    multi: true,
    options: [
      { label: "情報共有・社内検索" },
      { label: "議事録・資料づくり" },
      { label: "問い合わせ対応" },
      { label: "業務全般を効率化したい" },
      { label: "その他", other: true },
    ],
  },
  {
    key: "team",
    q: "AI推進の体制は？",
    options: [
      { label: "専任はいない", score: 0 },
      { label: "兼任が1名いる", score: 1 },
      { label: "小さなチームがある", score: 2 },
      { label: "推進体制が整っている", score: 3 },
    ],
  },
];

// 課題タグ別の一言（結果のパーソナライズ）
// キーのクォートは外さないこと。日本語キーを裸で書くと Terser が
// minify に失敗して本番ビルドが落ちる（npm run check の --unsafe が外しにくるので注意）。
const CHALLENGE_TIP = {
  "何から始めればいいか分からない":
    "まずは業務の棚卸しから。“どの業務にAIが効くか”の整理を一緒に行います。",
  "研修・導入しても現場に浸透しない":
    "ツール配布ではなく、会社の文脈をAIに渡し“使われる”状態まで伴走するのが効果的です。",
  "推進できる詳しい人がいない":
    "IT専任がいなくても大丈夫。導入から定着までLEAPSの伴走チームが支援します。",
  "必要な情報がすぐ探せない":
    "メール・Excel・社内ツールを横断し、出典つきで即回答できる基盤づくりが効きます。",
};

function phaseOf(score) {
  if (score <= 3)
    return {
      key: "A",
      name: "土台づくりフェーズ",
      lead: "情報がまだ散らばっていて、AIに渡す“土台”づくりから始めるのが効果的な段階です。",
      step: "Step1 業務整理 — 課題の洗い出しと、最初に取り組む範囲を決めるところから。",
      effect: "まずは“探す・聞く”の手間を減らし、小さな成功体験をつくります。",
      color: "#2B7FFF",
    };
  if (score <= 6)
    return {
      key: "B",
      name: "個人 → 組織への移行フェーズ",
      lead: "個人利用は始まっていますが、“組織で使える”には会社の文脈を渡す基盤が必要な段階です。",
      step: "Step2 設計・構築 — 情報の統合・データ設計で、自社専用AIの基盤をつくります。",
      effect: "属人化の解消と、知識資産がAIに蓄積される状態へ。",
      color: "#5A6CF5",
    };
  return {
    key: "C",
    name: "組織展開・加速フェーズ",
    lead: "土台は整いつつあります。全社定着と他部署への横展開を加速できる段階です。",
    step: "Step3-4 定着・横展開 — 全社レクチャーと運用改善で“使われる”を広げます。",
    effect: "個人活用では出せない、組織活用の複利効果を狙えます。",
    color: "#18A86B",
  };
}

const card = {
  background: "#fff",
  borderRadius: 24,
  boxShadow: "0 16px 40px rgba(27,35,48,0.08)",
  border: "1px solid #F0F3F8",
};

const PRIVACY_URL = "/privacy";

// 回答からスコア・フェーズ・引き継ぎ用メッセージを生成
function buildDiagnosis(answers, taskOther) {
  const score = QUESTIONS.reduce((sum, q) => {
    const a = answers[q.key];
    return sum + (a && typeof a.score === "number" ? a.score : 0);
  }, 0);
  const phase = phaseOf(score);
  const taskLabels = (Array.isArray(answers.task) ? [...answers.task] : []).map(
    (l) =>
      l === "その他" && taskOther.trim() ? `その他：${taskOther.trim()}` : l,
  );
  const message = [
    "【無料AI診断の結果】",
    `タイプ${phase.key}：${phase.name}`,
    `・従業員規模：${answers.size?.label || "-"}`,
    `・現在のAI活用：${answers.usage?.label || "-"}`,
    `・社内情報の状態：${answers.data?.label || "-"}`,
    `・一番の課題：${answers.challenge?.label || "-"}`,
    `・AIに任せたい業務：${taskLabels.join("、") || "-"}`,
    `・推進体制：${answers.team?.label || "-"}`,
    "",
    "この診断結果をもとに相談したいです。",
  ].join("\n");
  return { score, phase, taskLabels, message };
}

function AiAdoptionDiagnosis() {
  // step: 0..QUESTIONS.length-1 = 設問 / 'email' / 'result'
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [taskOther, setTaskOther] = useState("");
  const [form, setForm] = useState({
    company: "",
    name: "",
    email: "",
    tel: "",
  });
  const [agreed, setAgreed] = useState(false);

  // title/meta は App.js が siteMeta.js の ROUTE_META から適用する。
  useEffect(() => {
    ensureFonts();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const total = QUESTIONS.length;
  const isQuestion = typeof step === "number";

  const advance = () => {
    if (step < total - 1) setStep(step + 1);
    else setStep("email");
  };

  // 単一選択：選んだら即次へ
  const choose = (q, opt) => {
    setAnswers((s) => ({ ...s, [q.key]: opt }));
    advance();
  };

  // 複数選択：トグル（即進まない）
  const toggleMulti = (q, opt) => {
    setAnswers((s) => {
      const cur = Array.isArray(s[q.key]) ? [...s[q.key]] : [];
      const i = cur.indexOf(opt.label);
      if (i >= 0) cur.splice(i, 1);
      else cur.push(opt.label);
      return { ...s, [q.key]: cur };
    });
  };

  const back = () => {
    if (step === "result") return;
    if (step === "email") setStep(total - 1);
    else if (step > 0) setStep(step - 1);
  };

  const submitEmail = (e) => {
    e.preventDefault();
    if (!agreed) return;
    // 「結果を見る」のタイミングで裏側で自動送信（1回）。
    const { message } = buildDiagnosis(answers, taskOther);
    submitHubSpotForm({
      pageName: "AI導入支援 無料診断",
      ...(HUBSPOT_DIAGNOSIS_FORM_ID
        ? { formId: HUBSPOT_DIAGNOSIS_FORM_ID }
        : {}),
      fields: {
        email: form.email,
        firstname: form.name,
        company: form.company,
        phone: form.tel,
        message,
      },
    }).catch((err) => console.warn(err));
    // 自動返信と社内通知。HubSpotとは独立。
    sendLeadMail("diagnosis", {
      email: form.email,
      name: form.name,
      company: form.company,
      tel: form.tel,
      message,
    });
    setStep("result");
    window.scrollTo(0, 0);
  };

  const { phase } = buildDiagnosis(answers, taskOther);
  const challengeTip = CHALLENGE_TIP[answers.challenge?.label];

  // 「この結果をもとに無料相談する」→ 結果表示時(「結果を見る」)に送信済みのため、
  // 再送せず完了画面を表示する（二重送信防止）。
  const goConsult = () => {
    setStep("done");
    window.scrollTo(0, 0);
  };

  const progress = isQuestion
    ? (step / total) * 100
    : step === "email"
      ? (total / (total + 1)) * 100
      : 100;

  return (
    <div
      style={{
        fontFamily: "'Noto Sans JP', system-ui, sans-serif",
        background:
          "radial-gradient(900px 500px at 78% 8%, #E6F0FF 0%, transparent 55%), #FFFFFF",
        color: INK,
        lineHeight: 1.7,
        minHeight: "100vh",
      }}
    >
      {/* NAV */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid #EEF1F6",
        }}
      >
        <div
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            height: 70,
            padding: "0 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <a
            href="/media/"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              textDecoration: "none",
            }}
          >
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: PRIMARY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(43,127,255,0.32)",
              }}
            >
              <BrandMark />
            </span>
            <span
              style={{
                fontFamily: round,
                fontWeight: 800,
                fontSize: 18,
                color: INK,
              }}
            >
              LEAPS<span style={{ color: PRIMARY }}> Media</span>
            </span>
          </a>
        </div>
      </div>

      <div
        style={{ maxWidth: 600, margin: "0 auto", padding: "48px 20px 80px" }}
      >
        {/* 進捗バー（結果・完了以外） */}
        {step !== "result" && step !== "done" && (
          <div style={{ marginBottom: 28 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontFamily: round,
                fontSize: 12.5,
                fontWeight: 700,
                color: "#98A1B2",
                marginBottom: 8,
              }}
            >
              <span style={{ color: PRIMARY }}>3分でわかる無料AI診断</span>
              <span>
                {isQuestion ? step + 1 : total + 1} / {total + 1}
              </span>
            </div>
            <div
              style={{ height: 6, borderRadius: 999, background: "#E8EEF7" }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  borderRadius: 999,
                  background: PRIMARY,
                  transition: "width .3s ease",
                }}
              />
            </div>
          </div>
        )}

        {/* 設問 */}
        {isQuestion && (
          <div style={{ ...card, padding: 30 }}>
            <div
              style={{
                fontFamily: round,
                fontSize: 13,
                fontWeight: 700,
                color: "#98A1B2",
                marginBottom: 10,
              }}
            >
              Q{step + 1}
            </div>
            <h1
              style={{
                fontFamily: round,
                fontSize: 22,
                fontWeight: 800,
                margin: "0 0 22px",
              }}
            >
              {QUESTIONS[step].q}
            </h1>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {QUESTIONS[step].options.map((opt) => {
                const q = QUESTIONS[step];
                const selected = q.multi
                  ? (answers[q.key] || []).includes(opt.label)
                  : answers[q.key]?.label === opt.label;
                return (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() =>
                      q.multi ? toggleMulti(q, opt) : choose(q, opt)
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 11,
                      textAlign: "left",
                      fontFamily: round,
                      fontSize: 15,
                      fontWeight: 700,
                      color: selected ? "#fff" : INK,
                      background: selected ? PRIMARY : "#F6F9FE",
                      border: `2px solid ${selected ? PRIMARY : "#EEF1F6"}`,
                      borderRadius: 14,
                      padding: "16px 18px",
                      cursor: "pointer",
                      transition: "all .15s ease",
                    }}
                  >
                    {q.multi && (
                      <span
                        style={{
                          width: 20,
                          height: 20,
                          flexShrink: 0,
                          borderRadius: 6,
                          border: `2px solid ${selected ? "#fff" : "#C9D3E3"}`,
                          background: selected
                            ? "rgba(255,255,255,0.25)"
                            : "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                        }}
                      >
                        {selected ? "✓" : ""}
                      </span>
                    )}
                    {opt.label}
                  </button>
                );
              })}

              {/* その他の自由入力 */}
              {QUESTIONS[step].multi &&
                (answers[QUESTIONS[step].key] || []).includes("その他") && (
                  <input
                    type="text"
                    value={taskOther}
                    onChange={(e) => setTaskOther(e.target.value)}
                    placeholder="その他の内容をご記入ください"
                    style={{
                      width: "100%",
                      padding: "13px 15px",
                      fontSize: 15,
                      color: INK,
                      border: "1.5px solid #E4E9F1",
                      borderRadius: 12,
                      outline: "none",
                      fontFamily: "inherit",
                    }}
                  />
                )}
            </div>

            {/* 複数選択は「次へ」で進む */}
            {QUESTIONS[step].multi && (
              <button
                type="button"
                onClick={advance}
                disabled={(answers[QUESTIONS[step].key] || []).length === 0}
                style={{
                  width: "100%",
                  marginTop: 18,
                  fontFamily: round,
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#fff",
                  padding: 15,
                  borderRadius: 999,
                  border: "none",
                  cursor: "pointer",
                  background: (answers[QUESTIONS[step].key] || []).length
                    ? PRIMARY
                    : "#C9D3E3",
                  boxShadow: (answers[QUESTIONS[step].key] || []).length
                    ? "0 10px 24px rgba(43,127,255,0.34)"
                    : "none",
                }}
              >
                次へ
              </button>
            )}

            {step > 0 && (
              <button
                type="button"
                onClick={back}
                style={{
                  marginTop: 16,
                  fontFamily: round,
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: "#98A1B2",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                ← 前の質問に戻る
              </button>
            )}
          </div>
        )}

        {/* メール入力（必須） */}
        {step === "email" && (
          <div style={{ ...card, padding: 32 }}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <h1
                style={{
                  fontFamily: round,
                  fontSize: 23,
                  fontWeight: 800,
                  margin: "0 0 10px",
                }}
              >
                診断結果を表示します
              </h1>
              <p style={{ fontSize: 14, color: "#5B6472", margin: 0 }}>
                結果の表示とあわせて、詳細レポートをメールでもお送りします。
              </p>
            </div>
            <form
              onSubmit={submitEmail}
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
            >
              {[
                { k: "company", label: "会社名", type: "text", required: true },
                { k: "name", label: "お名前", type: "text", required: true },
                {
                  k: "email",
                  label: "メールアドレス",
                  type: "email",
                  required: true,
                },
                { k: "tel", label: "電話番号", type: "tel", required: false },
              ].map((f) => (
                <label key={f.k} style={{ display: "block" }}>
                  <span
                    style={{
                      display: "block",
                      fontFamily: round,
                      fontSize: 13.5,
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    {f.label}
                    {f.required && <span style={{ color: PRIMARY }}> ＊</span>}
                  </span>
                  <input
                    type={f.type}
                    required={f.required}
                    value={form[f.k]}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, [f.k]: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      padding: "13px 15px",
                      fontSize: 15,
                      color: INK,
                      border: "1.5px solid #E4E9F1",
                      borderRadius: 12,
                      outline: "none",
                      fontFamily: "inherit",
                    }}
                  />
                </label>
              ))}
              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  fontSize: 13,
                  color: "#5B6472",
                  cursor: "pointer",
                  marginTop: 4,
                }}
              >
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }}
                />
                <span>
                  <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: PRIMARY, fontWeight: 700 }}
                  >
                    プライバシーポリシー
                  </a>
                  に同意します
                </span>
              </label>
              <button
                type="submit"
                disabled={!agreed}
                style={{
                  marginTop: 6,
                  fontFamily: round,
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#fff",
                  padding: 16,
                  borderRadius: 999,
                  background: agreed ? PRIMARY : "#C9D3E3",
                  border: "none",
                  cursor: agreed ? "pointer" : "not-allowed",
                  boxShadow: agreed
                    ? "0 10px 24px rgba(43,127,255,0.34)"
                    : "none",
                }}
              >
                結果を見る
              </button>
              <button
                type="button"
                onClick={back}
                style={{
                  fontFamily: round,
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: "#98A1B2",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                ← 前に戻る
              </button>
              <p
                style={{
                  fontSize: 12,
                  color: "#98A1B2",
                  textAlign: "center",
                  margin: 0,
                }}
              >
                入力データは外部のAI学習には使われません。
              </p>
            </form>
          </div>
        )}

        {/* 結果 */}
        {step === "result" && (
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div
              style={{
                background: `linear-gradient(135deg, ${phase.color} 0%, #5A6CF5 100%)`,
                padding: "36px 30px",
                color: "#fff",
              }}
            >
              <div
                style={{
                  fontFamily: round,
                  fontSize: 13,
                  fontWeight: 700,
                  opacity: 0.9,
                  marginBottom: 8,
                }}
              >
                あなたの組織は… タイプ{phase.key}
              </div>
              <div style={{ fontFamily: round, fontSize: 26, fontWeight: 800 }}>
                {phase.name}
              </div>
            </div>
            <div style={{ padding: 30 }}>
              <p style={{ fontSize: 15, lineHeight: 1.9, margin: "0 0 22px" }}>
                {phase.lead}
              </p>

              <div
                style={{
                  background: "#F6F9FE",
                  borderRadius: 16,
                  padding: "18px 20px",
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    fontFamily: round,
                    fontSize: 13,
                    fontWeight: 700,
                    color: PRIMARY,
                    marginBottom: 6,
                  }}
                >
                  おすすめの第一歩
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.8 }}>
                  {phase.step}
                </div>
              </div>

              {challengeTip && (
                <div
                  style={{
                    background: "#F6F9FE",
                    borderRadius: 16,
                    padding: "18px 20px",
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{
                      fontFamily: round,
                      fontSize: 13,
                      fontWeight: 700,
                      color: PRIMARY,
                      marginBottom: 6,
                    }}
                  >
                    あなたの課題への打ち手
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.8 }}>
                    {challengeTip}
                  </div>
                </div>
              )}

              <div
                style={{
                  background: "#F1FBF6",
                  borderRadius: 16,
                  padding: "18px 20px",
                  marginBottom: 26,
                }}
              >
                <div
                  style={{
                    fontFamily: round,
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#18A86B",
                    marginBottom: 6,
                  }}
                >
                  期待できる効果
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.8 }}>
                  {phase.effect}
                </div>
              </div>

              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <button
                  type="button"
                  onClick={goConsult}
                  style={{
                    textAlign: "center",
                    fontFamily: round,
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#fff",
                    padding: 16,
                    borderRadius: 999,
                    background: PRIMARY,
                    border: "none",
                    cursor: "pointer",
                    boxShadow: "0 10px 24px rgba(43,127,255,0.34)",
                  }}
                >
                  この結果をもとに無料相談する
                </button>
                <a
                  href="/document"
                  style={{
                    textAlign: "center",
                    fontFamily: round,
                    fontSize: 16,
                    fontWeight: 700,
                    color: INK,
                    padding: 15,
                    borderRadius: 999,
                    background: "#fff",
                    border: "2px solid #E4E9F1",
                    textDecoration: "none",
                  }}
                >
                  資料をダウンロード
                </a>
              </div>
              <p
                style={{
                  fontSize: 12,
                  color: "#98A1B2",
                  textAlign: "center",
                  margin: "16px 0 0",
                }}
              >
                ※
                診断はおおまかな目安です。詳しくは無料相談で個別にご提案します。
              </p>
            </div>
          </div>
        )}

        {/* 完了画面（「無料相談する」押下後）。結果表示時に送信済みのため再送なし。 */}
        {step === "done" && (
          <div style={{ ...card, padding: "56px 32px", textAlign: "center" }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 999,
                background: "#DCF6E9",
                color: "#18A86B",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
                fontSize: 28,
                fontWeight: 700,
              }}
            >
              ✓
            </div>
            <h1
              style={{
                fontFamily: round,
                fontSize: 24,
                fontWeight: 800,
                margin: "0 0 12px",
              }}
            >
              ご相談を受け付けました
            </h1>
            <p
              style={{
                fontSize: 15,
                color: "#5B6472",
                lineHeight: 1.9,
                margin: 0,
              }}
            >
              診断結果とご入力内容をもとに、担当より2〜3営業日以内にご連絡します。
              <br />
              ありがとうございました。
            </p>
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div style={{ background: "#F6F9FE", borderTop: "1px solid #EEF1F6" }}>
        <div
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            padding: 28,
            textAlign: "center",
            fontSize: 12,
            color: "#98A1B2",
          }}
        >
          運営会社：株式会社LEAPS ／ 東京都千代田区神田神保町1-41 ／ © 2026
          Leaps Inc.
        </div>
      </div>
    </div>
  );
}

export default AiAdoptionDiagnosis;
